import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CalendarDays, Clock, Settings, ShieldAlert } from 'lucide-react';
import Logo from './Logo';
import { api, getTrialStatus } from '../api';

interface UserLayoutProps {
  children: React.ReactNode;
  title?: string;
  showLogo?: boolean;
}

const UserLayout: React.FC<UserLayoutProps> = ({ children, title, showLogo = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showBusinessFeatures, setShowBusinessFeatures] = useState(false);
  const [isPlanExpired, setIsPlanExpired] = useState(false);

  useEffect(() => {
      const checkAccess = async () => {
          try {
              const stores = await api.getStores();
              if (stores.length > 0) {
                  const store = stores[0];
                  const trial = getTrialStatus(store);
                  const isPaid = !!store.stripeSubscriptionId;
                  // トライアル終了かつ未課金の場合は全機能を制限
                  if (trial.isExpired && !isPaid) {
                      setIsPlanExpired(true);
                      return;
                  }
                  // ビジネスプランまたはトライアル中であれば勤怠機能を表示
                  const isBusinessEnabled = store.plan === 'business' || (!trial.isExpired && !isPaid);
                  setShowBusinessFeatures(isBusinessEnabled);
              }
          } catch(e) {
              console.error(e);
          }
      };
      checkAccess();
  }, []);

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-100 flex justify-center font-sans text-slate-700">
      <div className="w-full max-w-[480px] bg-white min-h-screen shadow-md relative flex flex-col">

        {/* Header */}
        <header className="bg-white pt-6 pb-4 px-6 flex items-center justify-center border-b border-transparent sticky top-0 z-10">
            {showLogo ? (
                <Logo size="sm" />
            ) : (
                <h1 className="text-lg font-bold text-slate-800">{title}</h1>
            )}
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto pb-24">
            {isPlanExpired ? (
                // トライアル終了・未課金の場合は全機能を停止
                <div className="min-h-[80vh] flex flex-col items-center justify-center p-8 text-center animate-in">
                    <div className="w-20 h-20 bg-orange-50 text-orange-400 rounded-full flex items-center justify-center mb-6 border border-orange-100">
                        <ShieldAlert size={40} />
                    </div>
                    <h2 className="text-xl font-black text-slate-800 mb-3">サービスが停止中です</h2>
                    <p className="text-slate-500 text-sm font-bold leading-relaxed">
                        無料トライアル期間が終了しました。<br />
                        管理者にプランの契約をご依頼ください。
                    </p>
                </div>
            ) : children}
        </main>

        {/* Bottom Navigation（プラン停止中は非表示） */}
        {!isPlanExpired && (
            <div className="fixed bottom-0 w-full max-w-[480px] bg-white border-t border-gray-200 flex justify-around items-center h-16 z-30 pb-safe">
                <button
                    onClick={() => navigate('/user/shifts')}
                    className={`flex flex-col items-center justify-center w-full h-full ${isActive('/user/shifts') ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                >
                    <CalendarDays size={24} strokeWidth={isActive('/user/shifts') ? 2.5 : 2} />
                    <span className="text-[10px] font-bold mt-1">シフト</span>
                </button>

                {showBusinessFeatures && (
                    <button
                        onClick={() => navigate('/user/attendance')}
                        className={`flex flex-col items-center justify-center w-full h-full ${isActive('/user/attendance') ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        <Clock size={24} strokeWidth={isActive('/user/attendance') ? 2.5 : 2} />
                        <span className="text-[10px] font-bold mt-1">打刻</span>
                    </button>
                )}

                <button
                    onClick={() => navigate('/user/settings')}
                    className={`flex flex-col items-center justify-center w-full h-full ${isActive('/user/settings') ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                >
                    <Settings size={24} strokeWidth={isActive('/user/settings') ? 2.5 : 2} />
                    <span className="text-[10px] font-bold mt-1">設定</span>
                </button>
            </div>
        )}
      </div>
    </div>
  );
};

export default UserLayout;
